import React, { useState } from "react";
import ImageUploader from "./components/ImageUploader";
import NamePositionMarker from "./components/NamePositionMarker";
import NameList from "./components/NameList";
import Preview from "./components/Preview";
import DownloadButton from "./components/DownloadButton";
import SettingsPanel from "./components/SettingsPanel";
import "./index.css";

function App() {
  const [image, setImage] = useState(null);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [names, setNames] = useState([]);
  const [currentNameIndex, setCurrentNameIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isWebP, setIsWebP] = useState(true);
  const [fontSettings, setFontSettings] = useState({
    family: "Roboto",
    size: 24,
    color: "#000000",
    customFonts: [],
  });

  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result);
      setPosition({ x: 50, y: 50 });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <img src="/logo.png" alt="Logo" className="h-28 mx-auto" />
      <div className="text-center mb-8">
      
      <h1 className="text-2xl font-bold text-center text-gray-900 mb-1">
        Automated Name Placement Tool
      </h1>
      <div className="p-4 mt-4 bg-orange-100 text-orange-800 rounded-md max-w-xl mx-auto">
      <span className="text-sm">For the best experience and full compatibility, <strong>Please use a Desktop Browser.</strong><br/>Mobile browsers may not support all features correctly.</span>
      </div>
      </div>
      <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          {!image ? (
            <ImageUploader onUpload={handleImageUpload} />
          ) : (
            <>
              <NamePositionMarker
                image={image}
                position={position}
                setPosition={setPosition}
              />
              <button
                onClick={() => setImage(null)}
                className="mt-4 w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Change Image
              </button>
            </>
          )}
          <div className="relative mt-8 flex justify-center items-center shadow-lg rounded-md  sm:rounded-2xl">
            <a href="https://bit.ly/play_wordchains_today" target="_blank" rel="noopener noreferrer">
              <img
                src="/play.wordchains.today.gif"
                alt="Play Wordchains Today"
                className="w-100 rounded-md  sm:rounded-2xl"
              />
            </a>
          </div>

          <div className="relative mt-4 flex justify-center items-center shadow-lg rounded-md  sm:rounded-2xl">
            <a href="mailto:hello@isuru.info" target="_blank" rel="noopener noreferrer">
              <img
                src="/your_ad_here.jpg"
                alt="Your Ad Here"
                className="w-100 rounded-md  sm:rounded-2xl"
              />
            </a>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
          <NameList names={names} setNames={setNames} />

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Font Settings</h3>
            <SettingsPanel
              fontSettings={fontSettings}
              setFontSettings={setFontSettings}
            />
          </div>
          {image && names.length > 0 && (
            <>
              <Preview
                image={image}
                position={position}
                name={names[currentNameIndex]}
                fontSettings={fontSettings}
              />

              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={() =>
                    setCurrentNameIndex((prev) => Math.max(0, prev - 1))
                  }
                  className="bg-secondary hover:bg-secondary-dark text-white px-4 py-2 rounded-md"
                >
                  Previous
                </button>
                <span className="font-medium">
                  {currentNameIndex + 1} / {names.length}
                </span>
                <button
                  onClick={() =>
                    setCurrentNameIndex((prev) =>
                      Math.min(names.length - 1, prev + 1)
                    )
                  }
                  className="bg-secondary hover:bg-secondary-dark text-white px-4 py-2 rounded-md"
                >
                  Next
                </button>
              </div>

              <div className="flex justify-center pt-4 flex-col sm:flex-row items-center gap-4">
      {/* WebP Option */}
      <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="format"
          value="webp"
          checked={isWebP}
          onChange={() => setIsWebP(true)}
          className="hidden"
        />
        <div className={`flex items-center justify-center w-6 h-6 border-2 rounded-full 
          ${isWebP ? 'border-blue-500' : 'border-gray-300'}`}>
          <div className={`w-3 h-3 rounded-full ${isWebP ? 'bg-blue-500' : 'hidden'}`} />
        </div>
        <span className={`select-none ${isWebP ? 'text-blue-500' : 'text-gray-500'}`}>
          WebP
        </span>
      </label>

      {/* PNG Option */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="format"
          value="png"
          checked={!isWebP}
          onChange={() => setIsWebP(false)}
          className="hidden"
        />
        <div className={`flex items-center justify-center w-6 h-6 border-2 rounded-full 
          ${!isWebP ? 'border-blue-500' : 'border-gray-300'}`}>
          <div className={`w-3 h-3 rounded-full ${!isWebP ? 'bg-blue-500' : 'hidden'}`} />
        </div>
        <span className={`select-none ${!isWebP ? 'text-blue-500' : 'text-gray-500'}`}>
          PNG
        </span>
      </label>
      </div>
      <DownloadButton
                image={image}
                position={position}
                names={names}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                fontSettings={fontSettings}
                isWebP={isWebP}
              />
    </div>

              
            </>
          )}
        </div>
      </div>
      <footer className="mt-8 text-center text-gray-600">
        <p>&copy; 2025 Invito - Automated Name Placement Tool.</p>
        <p>
          Made with ❤️ by{" "}
          <a className="font-semibold" href="https://isuru.info">
            Isuru Prabath
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
